import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './Navigation.css';

interface NavigationProps {
  onSectionChange: (section: string) => void;
}

export const Navigation: React.FC<NavigationProps> = ({ onSectionChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const baseNavItems = [
    { section: 'overview', label: 'Overview →', color: 'blue' },
    { section: 'transportation', label: 'Transportation →', color: 'light-blue' },
    { section: 'buildings', label: 'Buildings →', color: 'gold' },
    { section: 'other', label: 'Waste +', color: 'orange' },
    { section: 'people', label: 'People →', color: 'pink' },
    { section: 'ghg', label: 'GHG inventory →', color: 'teal' }
  ];

  // Policy Tool is now available to all users
  const navItems = [
    ...baseNavItems,
    { section: 'policy-tool', label: 'Policy Tool →', color: 'purple' }
  ];

  const handleNavClick = (section: string) => {
    // Check if we're on Overview page and if it's fully loaded
    const isOnOverviewPage = location.pathname === '/' || location.pathname === '/overview';
    const overviewFullyLoaded = typeof window !== 'undefined' && (window as any).overviewFullyLoaded === true;
    
    // If on Overview page and not fully loaded, block navigation to pages other than Transportation
    if (isOnOverviewPage && !overviewFullyLoaded && section !== 'transportation' && section !== 'overview') {
      alert('Please wait until the Overview page fully loads before navigating to other pages. The Transportation page is available.');
      return;
    }
    
    onSectionChange(section);
    // Navigate to /overview for overview dashboard, otherwise to section path
    if (section === 'overview') {
      navigate('/overview');
    } else {
      navigate(`/${section}`);
    }
    // Close mobile menu when navigating
    setIsMobileMenuOpen(false);
    // Scroll to top when navigating to a new section
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCurrentSection = () => {
    const path = location.pathname;
    if (path === '/' || path === '/overview') return 'overview';
    return path.slice(1);
  };

  const currentSection = getCurrentSection();

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen) {
        const target = event.target as Element;
        if (!target.closest('.nav-bar')) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  return (
    <div className="nav-bar">
      {/* Mobile Title */}
      <div className="mobile-nav-title">
        ClimatePlans
      </div>

      {/* Mobile Hamburger Button */}
      <button 
        className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        aria-label="Toggle navigation menu"
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>

      {/* Navigation Items */}
      <div className={`nav-items ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
        {navItems.map((item) => (
          <div
            key={item.section}
            className={`nav-item ${item.color} ${currentSection === item.section ? 'active' : ''}`}
            onClick={() => handleNavClick(item.section)}
          >
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};
